import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { type SubmitHandler } from 'react-hook-form/dist/types';
import { useNavigate } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowBackIosNew } from '@mui/icons-material';
import { Alert, Tooltip } from '@mui/material';
import { AxiosError } from 'axios';
import { HelpCircle, Info } from 'lucide-react';
import z from 'zod';

import Button from '../../common/Button';
import Loading from '../../common/Loading';
import { extractValue, toastPromise } from '../../common/utils';
import {
  useGetPreDefinedExpenseById,
  useGetPreDefinedExpenses,
  useGetUserDefinedExpenseById,
  useGetUserDefinedExpenses,
  useUpdateUserDefinedExpense,
} from '../../hooks/tanstack/useExpenseHook';
import { useGetRouteParams } from '../../hooks/tanstack/useUtilsHook';
import FormInput from '../form/FormInput';
import FormQueryInput from '../form/FormQueryInput';
import FormSelect from '../form/FormSelect';
import WarningDialog from '../WarningDialog';

const projectExpensesDetailSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  group: z.number({
    message: 'Category is required',
  }),
  annualizedAdjustment: z.string().optional(),
  formula: z.string().min(1, 'Formula is required').default('0'),
  description: z.string().optional(),
});

type ProjectExpensesDetailType = z.infer<typeof projectExpensesDetailSchema>;

const ProjectExpensesDetail = () => {
  const { expenseId, orgId, projectId } = useGetRouteParams();

  // const [error, setError] = useState(Object || null);
  const [openCreateExpenseWarningDialog, setOpenCreateExpenseWarningDialog] =
    useState(false);

  const navigate = useNavigate();

  const form = useForm<ProjectExpensesDetailType>({
    resolver: zodResolver(projectExpensesDetailSchema),
    defaultValues: {
      name: '',
      group: NaN,
      formula: '',
      description: '',
      annualizedAdjustment: '0',
    },
  });

  const { reset, formState, setValue, watch, setError, clearErrors } = form;

  const { isDirty, errors } = formState;

  const formulaFieldName = 'formula';

  const formulaErrorMessage = errors?.[formulaFieldName]?.message;

  const { data: predefinedExpenses } = useGetPreDefinedExpenses(
    {
      orgId,
    },
    {
      enabled: !!orgId,
    }
  );

  const { data: predefinedExpense } = useGetPreDefinedExpenseById(
    {
      expenseId,
      orgId,
    },
    {
      enabled: !!expenseId && !!orgId,
    }
  );

  const { data: userdefinedExpense, isLoading: isLoadingUserDefinedExpense } =
    useGetUserDefinedExpenseById(
      {
        expenseId,
        orgId,
        projectId,
      },
      {
        enabled: !!expenseId && !!orgId && !!projectId,
      }
    );

  const { data: defaultDefinedExpenseSets } = useGetPreDefinedExpenses(
    {
      orgId,
    },
    {
      enabled: !!orgId,
    }
  );

  const { data: userDefinedExpenseSets } = useGetUserDefinedExpenses(
    {
      orgId,
      projectId,
    },
    {
      enabled: !!orgId && !!projectId,
    }
  );

  const {
    mutateAsync: updateUserDefinedExpense,
    isPending: isUpdatingUserDefinedExpense,
  } = useUpdateUserDefinedExpense({
    onSuccess() {
      clearErrors();
    },
    onError(error) {
      if (error instanceof AxiosError && error.response.data) {
        setError(formulaFieldName, {
          message: extractValue(
            error.response?.data as Record<string, string | string[]>
          ) as string,
        });
      }
    },
  });

  const expense = userdefinedExpense || {
    ...predefinedExpense,
    is_locked: false,
  };

  const groups = useMemo(
    () =>
      Array.from(new Set(predefinedExpenses?.map((p) => p.group.name)))
        .map((e) => {
          const expense = predefinedExpenses?.find((g) => g.group.name === e);

          if (!expense) {
            return null;
          }

          return {
            label: expense.group.name,
            value: expense.group.id,
          };
        })
        .filter((e) => e) as { label: string; value: number }[],
    [predefinedExpenses]
  );

  const handleGoBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (expense && expense.group) {
      reset({
        name: expense.name,
        group: expense.group.id,
        description: expense.description,
        formula: expense.formula,
      });
    }
  }, [expense]);

  useEffect(() => {
    if (watch(formulaFieldName).length === 0 && errors?.[formulaFieldName]) {
      setValue(formulaFieldName, '0');
      return;
    }
  }, [errors?.[formulaFieldName]]);

  const onSubmit: SubmitHandler<ProjectExpensesDetailType> = async (
    data: ProjectExpensesDetailType
  ) => {
    if (!isDirty) {
      clearErrors();
      return;
    }

    if (
      (data.group !== expense?.group?.id &&
        userDefinedExpenseSets?.some(
          (e) => data.name === e.name && data.group === e.group.id
        )) ||
      defaultDefinedExpenseSets?.some(
        (e) => data.name === e.name && data.group === e.group.id
      )
    ) {
      setError(formulaFieldName, {
        message:
          'Expense name and category already exist. Please choose another name or category.',
      });
      return;
    }

    try {
      const res = updateUserDefinedExpense({
        expenseId,
        orgId,
        projectId,
        data: {
          ...data,
          project: projectId,
        },
      });

      toastPromise({
        promise: res,
        content: 'Expense updated successfully!',
      });
    } catch (error) {
      setValue(formulaFieldName, '0');
    }
  };

  const isDisabled = expense?.is_locked;

  return (
    <FormProvider {...form}>
      <form className="px-12 relative flex flex-col">
        <div className="flex items-center justify-between">
          <div
            onClick={handleGoBack}
            className="flex items-center gap-4 cursor-pointer"
          >
            <ArrowBackIosNew
              style={{
                height: '16px',
                width: '16px',
              }}
            />
            <h1 className="text-2xl font-bold">{expense?.name}</h1>
          </div>
          <Button
            onClick={() => {
              if (!isDirty) {
                return;
              }
              setOpenCreateExpenseWarningDialog(true);
            }}
            variant="contained_blue"
            loading={isUpdatingUserDefinedExpense}
            disabled={
              isDisabled ||
              isUpdatingUserDefinedExpense ||
              isLoadingUserDefinedExpense
            }
          >
            Save
          </Button>
        </div>
        {isLoadingUserDefinedExpense ||
          (isUpdatingUserDefinedExpense && (
            <div className="absolute inset-0 bg-white/70 z-10 w-[100%] flex items-center justify-center">
              <Loading />
            </div>
          ))}
        <div>
          <div className="grid grid-cols-4 gap-2 py-4">
            <div className="col-span-2">
              <FormInput name="name" label="Name" disabled={isDisabled} />
            </div>
            <div className="col-span-2">
              <FormSelect
                options={groups}
                name="group"
                label="Category"
                disabled={isDisabled}
              />
            </div>
            {/* <div className="col-span-1">
                <FormInput
                  type="number"
                  name="annualizedAdjustment"
                  label="Annualized Adjustment"
                  disabled={true}
                />
              </div> */}
            <div className="col-span-4">
              <FormInput
                name="description"
                label="Description"
                multiline
                disabled={isDisabled}
                rows={4}
              />
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex gap-2 items-center">
              <h2 className="text-2xl font-bold">Formula</h2>
              <Tooltip
                title="Start by typing $ to bring up the variant selector. Choose your desired variant, then enter the mathematical operation you want to perform."
                placement="top-end"
                arrow
              >
                <HelpCircle size={20} color="#2196F3" />
              </Tooltip>
            </div>
            <div>
              <div className="flex gap-2 items-center">
                <Info size={20} color="#666666" />
                <p className="text-[#666666]">
                  This formula defines the rate table for your custom expenses,
                  which the system will automatically multiply by crew
                  requirements to calculate the custom expense of a given model.
                </p>
              </div>
            </div>
            <FormQueryInput name={formulaFieldName} disabled={isDisabled} />
          </div>
          <div className="py-4">
            {formulaErrorMessage && (
              <Alert severity="error">{formulaErrorMessage}</Alert>
            )}
          </div>
        </div>
        <WarningDialog
          open={openCreateExpenseWarningDialog}
          primaryActionText={'Continue'}
          handlePrimaryAction={async () => {
            await onSubmit(watch());
            setOpenCreateExpenseWarningDialog(false);
          }}
          handleClose={() => {
            setOpenCreateExpenseWarningDialog(false);
          }}
          handleSecondaryAction={() => {
            setOpenCreateExpenseWarningDialog(false);
          }}
          secondaryActionText="Cancel & Download Current File"
          content={
            <p>
              Editing a custom expense will make your current{' '}
              <span>Actual Data</span> file outdated. You'll need to download
              the new templates, update your data, and upload again.{' '}
            </p>
          }
        />
      </form>
    </FormProvider>
  );
};

export default ProjectExpensesDetail;
